import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { PhotoBookItem } from "@modules/ServiceProviders/Components/PhotoBook/PhotoBookItem";
import {
  Wizard,
  WizardContent,
  WizardHeader,
  WizardNav,
} from "@Shared/Components/Layouts/Wizard/Wizard";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { Container } from "@UI/Components/Container/Container";
import { VStack, HStack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { UnorderedList, ListItem } from "@UI/Components/List/List";
import { PhotoUploadBox } from "../../Components/PhotoBook/PhotoUploadBox";
import { Button } from "@UI/Components/Button/Button";
import { Modal } from "@UI/Components/Modal/Modal";
import { Spinner } from "@UI/Components/Spinner/Spinner";
import { AlertBox } from "@UI/Components/AlertBox/AlertBox";
import { Box } from "@UI/Components/Box/Box";
import { Icon } from "@UI/Components/Icon/Icon";
import { Xmark } from "@UI/Components/Icons/Xmark";
import { Plus } from "@UI/Components/Icons/Plus";

import { useDisclosure } from "@UI/hooks/useDisclosure";
import { useToast, ToastProps } from "@UI/hooks/useToast";
import { IPresignedUrlPayload } from "@modules/ServiceProviders/services/photoBook";
import { usePhotoBook } from "@modules/ServiceProviders/hooks/photoBook/usePhotoBook";

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "@modules/ServiceProviders/Components/messages";
import i18n from "@I18n/services/i18n";

export interface IPhoto
  extends Pick<IPresignedUrlPayload, "file_extension" | "rawFile"> {
  id: string;
  fullUrl?: string;
  thumbnailUrl?: string;
  blobUrl?: string;
  isChecked?: boolean;
}

export const ServiceProviderPhotoBookRouter = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [draggedItem, setDraggedItem] = useState<
    (IPhoto & { index: number }) | null
  >();

  const { isMobile } = useMediaQuery();

  const toast = useToast();

  const {
    photoPreview,
    setPhotoPreview,
    isDeleting,
    hasDeletedSuccessfully,
    isErrorDeleting,
    deletePhotoAttachment,
    portfolioPhoto,
    portfolioPhotoTotalCount,
    isLoadingPortfolio,
    isFetchingPortfolio,
    getMorePortfolioPhoto,
    incrementPagination,
  } = usePhotoBook();

  useEffect(() => {
    if (
      Array.isArray(portfolioPhoto) &&
      portfolioPhoto.length > 0 &&
      photoPreview?.length < portfolioPhotoTotalCount
    ) {
      setPhotoPreview((prev) => {
        return prev ? [...prev, ...portfolioPhoto] : portfolioPhoto;
      });
    }
  }, [portfolioPhoto]);

  const onSelect = (photoId: string, isChecked: boolean) => {
    const updated = photoPreview.map((item) =>
      item.id === photoId ? { ...item, isChecked } : item
    );
    setPhotoPreview(updated);
  };

  const onDeletePhoto = () => {
    const ids =
      Array.isArray(photoPreview) &&
      photoPreview.filter((item) => item.isChecked).map((item) => item.id);
    deletePhotoAttachment(ids);
  };

  useEffect(() => {
    if (hasDeletedSuccessfully) {
      showToast({
        status: "success",
        description: i18n.formatMessage(
          messages.photoBookDeletionSuccessMessage
        ),
      });
      const updated = photoPreview?.filter((item) => !item.isChecked);
      setPhotoPreview(updated);
      onClose();
    }
  }, [hasDeletedSuccessfully]);

  useEffect(() => {
    if (isErrorDeleting) {
      showToast({
        status: "error",
        description: i18n.formatMessage(messages.photoBookDeletionErrorMessage),
      });
      onClose();
    }
  }, [isErrorDeleting]);

  const showToast = (toastData: Pick<ToastProps, "status" | "description">) => {
    const { status, description } = toastData;
    const toastId = uuidv4();
    toast({
      id: toastId,
      status,
      position: isMobile ? "bottom" : "top-right",
      duration: 2000,
      isClosable: true,
      render: () => (
        <AlertBox
          status={status}
          background="white"
          childrenContainerStyle={{ justifyContent: "start" }}
          marginTop={!isMobile ? "80px" : "initial"}
        >
          <HStack justifyContent="space-between" flexGrow={1}>
            <Text m="0" ml={3} color="gray.700">
              {description}
            </Text>
            <Icon
              as={Xmark}
              color="gray.700"
              cursor="pointer"
              onClick={() => toast.close(toastId)}
            />
          </HStack>
        </AlertBox>
      ),
    });
  };

  const selectedItemsCount =
    photoPreview && photoPreview.filter((item) => item.isChecked).length;

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
      },
    })
  );

  const onDragStart = (event: DragEndEvent) => {
    if (!Array.isArray(photoPreview)) return;

    const { active } = event;
    const draggedItemIndex = photoPreview.findIndex(
      (item) => item.id === active.id
    );

    setDraggedItem({
      ...photoPreview[draggedItemIndex],
      index: draggedItemIndex,
    });
  };

  const onDragEnd = (event: DragEndEvent) => {
    if (!Array.isArray(photoPreview)) return;

    const { active, over } = event;

    let reorderedPortfolioPhotos;

    if (active && over && active.id !== over.id) {
      const oldIndex = photoPreview.findIndex((item) => item.id === active.id);
      const newIndex = photoPreview.findIndex((item) => item.id === over.id);

      reorderedPortfolioPhotos = arrayMove(photoPreview, oldIndex, newIndex);
    }

    reorderedPortfolioPhotos && setPhotoPreview(reorderedPortfolioPhotos);
    // handleDragEnd(reorderedPortfolioPhotos);
  };

  return (
    <Wizard paddingTop={{ base: 5, md: 8 }}>
      <WizardNav>
        <BackLink
          onClick={() => {
            window.location.replace(document.referrer);
          }}
        />
      </WizardNav>
      <WizardHeader marginBottom="0">
        <Container
          textAlign="start"
          px={{ base: 0 }}
          maxW={[
            "container.md",
            "container.sm",
            "container.md",
            "container.xl",
          ]}
        >
          <Heading fontSize={{ base: "1.75rem", lg: "3rem" }}>
            <FormattedMessage {...messages.photoBookTitle} />
          </Heading>
          <Text fontSize={{ base: "1rem", lg: "1.125rem" }}>
            <FormattedMessage {...messages.photoBookSubTitle} />
          </Text>
        </Container>
      </WizardHeader>
      <WizardContent>
        <HStack
          width="inherit"
          minH="32px"
          justifyContent="space-between"
          alignContent="center"
          mb={{ base: "12px", lg: "16px" }}
        >
          {selectedItemsCount > 0 && (
            <>
              <Heading size="sm" textAlign="start" fontFamily="Inter">
                {`${selectedItemsCount} ${i18n.formatMessage(
                  messages.photoBookSelectPhotoCountText
                )}`}
              </Heading>

              <Button size="sm" colorScheme="red" onClick={onOpen}>
                <FormattedMessage {...messages.photoBookDeleteButtonText} />
              </Button>
              <Modal
                isOpen={isOpen}
                onClose={onClose}
                closeOnOverlayClick={!isDeleting}
                header={
                  <VStack alignItems="start" spacing="16px">
                    <Heading size="lg">
                      <FormattedMessage
                        {...messages.photoBookDeleteConfirmationHeader}
                      />
                    </Heading>
                    <Text fontSize="md" fontWeight="normal">
                      <FormattedMessage {...messages.photoBookDeleteMessage} />
                    </Text>
                  </VStack>
                }
              >
                <VStack alignItems="center" spacing="8px" mb="20px">
                  <Button
                    w="full"
                    size="lg"
                    colorScheme="seaGreen"
                    onClick={onDeletePhoto}
                    isLoading={isDeleting}
                    isDisabled={isDeleting}
                  >
                    <FormattedMessage
                      {...messages.photoBookDeleteConfirmButton}
                    />
                  </Button>
                  <Button w="full" size="lg" bg="gray.300" onClick={onClose}>
                    <FormattedMessage
                      {...messages.photoBookDeleteCancelButton}
                    />
                  </Button>
                </VStack>
              </Modal>
            </>
          )}
        </HStack>

        <VStack mb={{ base: "20px", lg: "32px" }}>
          <HStack
            spacing="0"
            gap={{ base: "12px", lg: "16px" }}
            flexWrap="wrap"
            alignSelf="start"
          >
            <PhotoUploadBox
              variant="small"
              photoPreview={photoPreview}
              setPhotoPreview={setPhotoPreview}
              showToast={showToast}
              incrementPagination={incrementPagination}
            />
            {isLoadingPortfolio && (
              <VStack
                boxSize={{
                  base: "140px",
                  sm: "160px",
                  md: "165px",
                  lg: "236px",
                }}
                justifyContent="center"
              >
                <Spinner color="gray.500" size="lg" />
              </VStack>
            )}

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={photoPreview || []}
                strategy={rectSortingStrategy}
                disabled={
                  true
                  // Array.isArray(photoPreview) && photoPreview.length > 1
                  //   ? false
                  //   : true
                }
              >
                {!isLoadingPortfolio &&
                  Array.isArray(photoPreview) &&
                  photoPreview.length > 0 &&
                  photoPreview.map((item) => (
                    <SortableItem key={item.id} id={item.id}>
                      <PhotoBookItem
                        cursor="pointer"
                        boxSize={{
                          base: "140px",
                          sm: "160px",
                          md: "165px",
                          lg: "236px",
                        }}
                        url={item.fullUrl || item.blobUrl}
                        isChecked={item.isChecked}
                        onSelect={() => onSelect(item.id, !item.isChecked)}
                      />
                    </SortableItem>
                  ))}
              </SortableContext>

              <DragOverlay adjustScale={true}>
                {draggedItem && (
                  <PhotoBookItem
                    boxSize={{ base: "160px", lg: "236px" }}
                    url={draggedItem.fullUrl || draggedItem.blobUrl}
                  />
                )}
              </DragOverlay>
            </DndContext>
          </HStack>
          <Button
            variant="unstyled"
            color="pink.500"
            onClick={() => getMorePortfolioPhoto()}
            isLoading={!isLoadingPortfolio && isFetchingPortfolio}
            isDisabled={isFetchingPortfolio}
            display={
              photoPreview?.length > 0 &&
              portfolioPhotoTotalCount > photoPreview?.length
                ? "initial"
                : "none"
            }
          >
            <Icon as={Plus} mr="4px" />
            <FormattedMessage {...messages.photoBookSeeMorePhotos} />
          </Button>
        </VStack>

        <VStack alignItems="start">
          <Heading size="sm" textAlign="start" fontFamily="Inter">
            <FormattedMessage {...messages.photoBookInstructionTitle} />
          </Heading>
          <UnorderedList listStylePosition="inside" pl="7px">
            <ListItem>
              <FormattedMessage {...messages.photoBookInstruction1} />
            </ListItem>
            <ListItem>
              <FormattedMessage {...messages.photoBookInstruction2} />
            </ListItem>
            <ListItem>
              <FormattedMessage {...messages.photoBookInstruction3} />
            </ListItem>
            <ListItem>
              <FormattedMessage {...messages.photoBookInstruction4} />
            </ListItem>
            <ListItem>
              <FormattedMessage {...messages.photoBookInstruction5} />
            </ListItem>
          </UnorderedList>
        </VStack>
      </WizardContent>
    </Wizard>
  );
};

const SortableItem = (props: { children: React.ReactNode; id: string }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "pointer",
    transformOrigin: "0 0",
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      zIndex={isDragging ? "2" : "initial"}
      {...listeners}
      {...attributes}
    >
      {props.children}
    </Box>
  );
};
